import React from 'react'

import { LoaderPage } from 'components/pages/loading/Loading.page.component'

import { SignupContext, signupReducer } from 'context/signup'
import { UserContext } from 'context/user'
import { useRouter } from 'next/router'

import { SocialConfigProvider } from 'context/socialConfig'

import {
  IUser,
  IUserContactData,
  ResponseUserSave,
  SignupActionTypes,
  SignupState,
  SignupSteps,
} from 'interfaces'

import {
  completeUser,
  createUser,
  emailExists,
  getUserExistsById,
  updateUserByEmail,
  uploadUserAvatar,
  usernameExists,
} from 'services'
import { validateUsername } from 'shared/validations/formValidators'
import { EMAIL_ALREADY_TAKEN } from '../../shared/consts/texts/errors'
import { validateSignupForm } from '../../shared/utils/validators'
import { requestConnection } from '@/services/connections'
import { useAuthParams } from '@/components/pages/auth/hooks/authParams'
import { getProfileLink } from 'containers/profile/utilities/profile'

interface Props {
  children: React.ReactNode
}

const Signup_INITAL_SATATE: SignupState = {
  email: '',
  phone: '',
  step: SignupSteps.INITIAL,
  showReturnedUserModal: false,
}

export const SignupProvider: React.FC<Props> = ({ children }) => {
  const { setUser, user, loading } = React.useContext(UserContext)
  const [firstValidation, setFirstValidation] = React.useState(false)
  const [firstValidationUsername, setFirstValidationUsername] =
    React.useState(false)

  const { getAuthParams } = useAuthParams()

  const [state, dispatch] = React.useReducer(
    signupReducer,
    Signup_INITAL_SATATE
  )
  const router = useRouter()
  const {
    invitedUserToken,
    urlCallback,
    requestConnection: requestConnectionParam,
  } = router.query

  const stepsOrder = [
    SignupSteps.USER_INFO_FORM,
    SignupSteps.SOCIALS_FORM,
    SignupSteps.COMPLETE_PROFILE,
    SignupSteps.PHONE_NUMBER_STEP,
    SignupSteps.LOCATION_FORM,
    SignupSteps.CREATOR_TYPE,
    SignupSteps.CONTENT_TYPE,
    SignupSteps.COLLAB_TYPE,
  ]

  /**
   * --- Helpers
   */
  const cleanErrors = () =>
    dispatch({ type: SignupActionTypes.SET_ERROR, payload: '' })

  const closeReturnedUserModal = () => {
    dispatch({
      type: SignupActionTypes.SET_SHOW_RETURNED_USER_MODAL,
      payload: false,
    })
  }

  const getStepByIndex = (index: number) => stepsOrder[index]

  const getNextStep = () => {
    const currentStepIndex = stepsOrder.indexOf(state.step)
    return stepsOrder[currentStepIndex + 1]
  }

  const getPreviousStep = () => {
    const currentStepIndex = stepsOrder.indexOf(state.step)
    return stepsOrder[currentStepIndex - 1]
  }

  const onboarding = () => {
    setLoader(false)
    dispatch({
      type: SignupActionTypes.SET_STEP,
      payload: SignupSteps.PHONE_NUMBER_STEP,
    })
  }

  const setLoader = (isLoading: boolean) =>
    dispatch({ type: SignupActionTypes.SET_LOADER, payload: isLoading })

  const resetProcess = () => {
    cleanErrors()
    setLoader(false)
    dispatch({ type: SignupActionTypes.SET_CODE_SENT, payload: false })
    dispatch({ type: SignupActionTypes.SET_STEP, payload: SignupSteps.INITIAL })
  }

  /**
   * --- useEffect
   */
  React.useEffect(() => {
    if (!loading) {
      if (user?.id && !firstValidation) {
        validateUserExists(user)
      }
      setFirstValidation(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, firstValidation, loading])

  const saveUserData = async (userData: IUser): Promise<IUser> => {
    const bodyRequest = {
      ...userData,
      invitedUserToken: invitedUserToken as string,
    }

    const res = (
      userData.id
        ? await updateUserByEmail(userData)
        : await createUser(bodyRequest)
    ) as IUser

    setUser(res)
    return res
  }

  /**
   * --- Functions
   *  In alphabetical order
   */
  const addTags = async (u: IUser) => {
    const nextStep = getNextStep()
    const updatedUser = {
      ...user,
      ...u,
      signupStep: stepsOrder.indexOf(nextStep),
    }
    if (u.creatorType) {
      setUser(updatedUser)
      dispatch({
        type: SignupActionTypes.SET_STEP,
        payload: SignupSteps.CONTENT_TYPE,
      })
      return
    }

    if (u.contentType) {
      setUser(updatedUser)
      dispatch({
        type: SignupActionTypes.SET_STEP,
        payload: SignupSteps.COLLAB_TYPE,
      })
      return
    }

    if (u.collabInterest) {
      user.newUser && (await completeUser())
      const completedUser: IUser = {
        ...user,
        ...u,
        is_complete: true,
        newUser: false,
      }
      dispatch({ type: SignupActionTypes.SET_LOADER, payload: true })
      setUser(completedUser)
      await updateUserByEmail(completedUser)

      if (completedUser.referringUserID) {
        const existingUser = await getUserExistsById(user.referringUserID)

        if (existingUser) {
          completeReferredUser()
          return
        }
      }

      if (urlCallback) {
        let url = urlCallback as string
        if (requestConnectionParam) {
          url = url + '?requestConnection=true'
        }
        router.push(url)
        router.push(urlCallback as string)
        return
      }

      router.push(`/${completedUser?.username.toLocaleLowerCase()}`)

      return
    }
  }

  const backStep = async () => {
    const currentIndexStep = stepsOrder.indexOf(state.step)
    await saveUserData({ signupStep: currentIndexStep - 1 })
    dispatch({
      type: SignupActionTypes.SET_STEP,
      payload: getPreviousStep() || state.step,
    })
  }

  const continueStep = async (u: IUser) => {
    setLoader(true)
    const currentIndexStep = stepsOrder.indexOf(state.step)
    await saveUserData({ ...user, ...u, signupStep: currentIndexStep + 1 })
    setLoader(false)
    dispatch({
      type: SignupActionTypes.SET_STEP,
      payload: getNextStep() || state.step,
    })
  }

  const finishRegister = async (u?: IUser) => {
    setLoader(true)
    const userData = {
      ...user,
      ...u,
      followerCount: 0,
      signupStep: 3,
    }
    const res = (await saveUserData(userData)) as ResponseUserSave
    const id = res?.id ? res.id : res?.user?.id
    const discordUrl = res?.invite?.url

    setUser({ ...userData, id })

    if (discordUrl) {
      dispatch({
        type: SignupActionTypes.SET_DISCORD_INVITE,
        payload: discordUrl,
      })
    }
    if (id) {
      dispatch({
        type: SignupActionTypes.SET_STEP,
        payload: SignupSteps.COMPLETE_PROFILE,
      })
    }
    setLoader(true)
  }

  const skipOnboarding = async () => {
    setLoader(true)
    user.newUser && (await completeUser())
    const completedUser: IUser = { ...user, is_complete: true, newUser: false }

    if (completedUser.referringUserID) {
      const existingUser = await getUserExistsById(user.referringUserID)

      if (existingUser) {
        completeReferredUser()
        return
      }
    }

    await saveUserData(completedUser)

    // This could be used letter
    // if (urlCallback) {

    //   let url = urlCallback as string
    //   if (requestConnectionParam) {
    //     url = url + '?requestConnection=true'
    //   }
    //   router.push(url)
    //   router.push(urlCallback as string)
    //   return
    // }

    //
    // const authParams = getAuthParams()
    // const { affiliate_id } = authParams || {}
    // const { affiliate_id: currentAffiliate_id } = router.query || {}
    // const affiliateId = currentAffiliate_id || affiliate_id

    // const urlParam = affiliateId ? `?affiliate_id=${affiliateId}` : ''
    router.push(getProfileLink(completedUser))
    return

    // dispatch({
    //   type: SignupActionTypes.SET_STEP,
    //   payload: SignupSteps.FINAL_STEP,
    // })

    // setLoader(false)
  }

  // TODO: refactor referral logic to get data from localstorage
  const completeReferredUser = async () => {
    const userComplete: IUser = {
      socials: [],
      ...user,
      is_complete: true,
      authorized: true,
      newUser: false,
    }

    const resUser = await saveUserData(userComplete)
    await completeUser()

    if (resUser?.id) {
      const userRequestConnection = await requestConnection(
        user.referringUserID
      )
      if ('error' in userRequestConnection) return
      if (userRequestConnection?.id) {
        setUser({
          ...user,
          ...resUser,
          ...userRequestConnection,
        })
      } else {
        setUser({ ...user, ...resUser })
      }
      if (urlCallback) {
        let url = urlCallback as string
        if (requestConnectionParam) {
          url = url + '?requestConnection=true'
        }
        router.push(url)
        router.push(urlCallback as string)
        return
      }
      router.push(`/${userComplete?.username.toLocaleLowerCase()}`)
      return
    }
    setLoader(false)
  }

  const setUserContact = async (data: IUserContactData, avatar: File) => {
    setLoader(true)
    cleanErrors()

    const error = validateSignupForm(data)
    if (state?.singupErrors?.email || state?.singupErrors?.username) {
      setLoader(false)
      return
    }

    if (error) {
      dispatch({
        type: SignupActionTypes.SET_ERROR,
        payload: error.error,
      })
      setLoader(false)
      return
    }

    const referral_id = router?.query?.referral_id

    const userData = {
      ...user,
      ...data,
      phone: '',
      signupStep: 1,
      followerCount: 0,
      socials: [],
      newUser: user.id?.length ? false : true,
      usernameLower: data.username.toLowerCase(),
      referringUserID:
        user?.referringUserID || referral_id ? String(referral_id) : undefined,
    }

    setUser(userData)
    const resUser = await saveUserData({ ...user, ...userData })

    if (resUser?.id) {
      setUser({ ...user, ...resUser })
    }

    if (avatar) {
      const newUrlAvatar = await uploadUserAvatar(avatar)
      setUser({ ...user, ...resUser, avatar: newUrlAvatar })
    }

    dispatch({
      type: SignupActionTypes.SET_STEP,
      payload: SignupSteps.SOCIALS_FORM,
    })
    setLoader(false)
    return
  }

  const validateUserExists = (user: IUser | undefined) => {
    setUser(user)
    validateUserStep(user)
    setLoader(false)
  }

  const validateUserStep = (u: IUser) => {
    if (u?.id && u?.is_complete) {
      dispatch({
        type: SignupActionTypes.SET_STEP,
        payload: SignupSteps.FINAL_STEP,
      })
      return
    }

    if (u?.signupStep > 0) {
      dispatch({
        type: SignupActionTypes.SET_STEP,
        payload: getStepByIndex(u.signupStep),
      })
      dispatch({
        type: SignupActionTypes.SET_SHOW_RETURNED_USER_MODAL,
        payload: true,
      })
      return
    }
  }

  const validateExistingUsername = async (username: string) => {
    cleanErrors()
    dispatch({ type: SignupActionTypes.SET_VALIDATING_USERNAME, payload: true })
    const isValidUsername = validateUsername(username)
    const stopValidatingAndSetError = (error: boolean) => {
      dispatch({
        type: SignupActionTypes.SET_VALIDATING_USERNAME,
        payload: false,
      })
      dispatch({
        type: SignupActionTypes.SET_SINGUP_ERRORS,
        payload: { username: error },
      })
    }

    if (!isValidUsername) {
      dispatch({
        type: SignupActionTypes.SET_ERROR,
        payload:
          'Oops, that username is invalid. Usernames can only contain letters, numbers, “.”, and “_”.',
      })
      stopValidatingAndSetError(true)
      return false
    } else if (username !== user?.username || !firstValidationUsername) {
      setFirstValidationUsername(true)
      const exists = await usernameExists(username)

      if (exists) {
        dispatch({
          type: SignupActionTypes.SET_ERROR,
          payload: 'Username already exists',
        })
        stopValidatingAndSetError(true)
        return false
      }
    }

    stopValidatingAndSetError(false)
    return true
  }

  const validateExistingEmail = async (email: string) => {
    cleanErrors()
    dispatch({ type: SignupActionTypes.SET_VALIDATING_EMAIL, payload: true })
    const stopValidatingAndSetError = (error: boolean) => {
      dispatch({
        type: SignupActionTypes.SET_VALIDATING_EMAIL,
        payload: false,
      })
      dispatch({
        type: SignupActionTypes.SET_SINGUP_ERRORS,
        payload: { email: error },
      })
    }
    const exists = await emailExists(email)

    if (exists) {
      dispatch({
        type: SignupActionTypes.SET_ERROR,
        payload: EMAIL_ALREADY_TAKEN,
      })
      stopValidatingAndSetError(true)
      return
    }

    stopValidatingAndSetError(false)
  }

  /**
   * --- JSX
   */
  return (
    <SignupContext.Provider
      value={{
        ...state,
        addTags,
        backStep,
        closeReturnedUserModal,
        continueOnboarding: onboarding,
        continueStep,
        finishRegister,
        resetProcess,
        setUserContact,
        skipOnboarding,
        validateExistingEmail,
        validateExistingUsername,
      }}
    >
      <SocialConfigProvider>
        {firstValidation ? children : <LoaderPage />}
      </SocialConfigProvider>
    </SignupContext.Provider>
  )
}
